import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {TextInput, Dropdown} from '@/components/atoms';
import ProductSelectionModal from './ProductSelectionModal';
import styles from '../RewardSetup.module.scss';
import NonInteractiveDropdownTrigger from './NonInteractiveDropdownTrigger';

/**
 * BonusProductsCondition component displays the bonus products reward condition UI
 * Handles product selection via modal triggered by dropdown option selection, order quantity, and bonus product limit inputs
 *
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data containing selectedProducts, orderQty, limitOfProductQty
 * @param {Object} props.validation - Validation state object
 * @param {boolean} props.bonusProductsLoading - Loading state for bonus products
 * @param {Function} props.handleProductsChange - Handler for products selection changes
 * @param {Function} props.handleOrderQtyChange - Handler for order quantity changes
 * @param {Function} props.handleLimitOfProductQtyChange - Handler for limit of product qty changes
 * @param {string} props.orderQtyError - Error message for order quantity validation
 * @param {string} props.limitOfProductQtyError - Error message for limit of product qty validation
 * @returns {JSX.Element} The rendered BonusProductsCondition component
 */
const BonusProductsCondition = ({
  formData,
  validation,
  bonusProductsLoading,
  handleProductsChange,
  handleOrderQtyChange,
  handleLimitOfProductQtyChange,
  orderQtyError,
  limitOfProductQtyError
}) => {
  const [showProductModal, setShowProductModal] = useState(false);

  const handleOpenProductModal = () => {
    if (!bonusProductsLoading) setShowProductModal(true);
  };

  // Handle closing the product selection modal
  const handleCloseProductModal = () => {
    setShowProductModal(false);
  };

  // Handle product selection from modal
  const handleProductSelection = (selectedProductsData) => {
    // Convert selected products to the format expected by the parent component
    // The parent expects an array of objects with value and label properties
    const selectedProductValues = selectedProductsData.map((product) => ({
      value: product.productId,
      label: product.productName
    }));

    handleProductsChange(selectedProductValues);
  };
  // Create display options for dropdown based on selected products
  // formData.selectedProducts now stores full product objects with value and label
  const selectedProductOptions = formData.selectedProducts || [];

  return (
    <div className='flex flex-wrap gap-[13px]'>
      <div
        className={`${styles.rewardSetup__productsDropdownWrapper} text-left`}>
        <NonInteractiveDropdownTrigger
          id='bonus-products-dropdown-trigger'
          testId='bonus-products-dropdown-trigger'
          ariaLabel='Open product selection'
          onOpen={handleOpenProductModal}>
          <Dropdown
            className={`${styles.rewardSetup__products_dropdown} text-sm placeholder:text-sm`}
            value={selectedProductOptions} // Show selected products
            isError={validation.selectedProductsValid}
            errorMessage={
              validation.selectedProductsValid
                ? 'At least one product must be selected'
                : ''
            }
            isDark={true}
            childList={[]}
            placeholder={
              bonusProductsLoading ? 'Loading...' : 'Select Products'
            }
            isJsonWrap={false}
            selection='multiple'
            hideMenuListOnSelection={true}
            isClearable={false}
            preventTyping={true}
            updateValue={() => {}}
          />
        </NonInteractiveDropdownTrigger>
      </div>
      <span className='mt-2 whitespace-nowrap text-sm'>free after every</span>
      <div className={`w-32 ${styles.rewardSetup__inputPoints}`}>
        <TextInput
          className='text-sm placeholder:text-sm'
          value={formData.orderQty}
          onChange={handleOrderQtyChange}
          label=''
          placeholderText='Qty'
          error={orderQtyError}
          isDark={true}
          type='number'
          id='order-quantity'
        />
      </div>
      <span className='mt-2 whitespace-nowrap text-sm'>
        orders, with a limit of
      </span>
      <div className={`w-32 ${styles.rewardSetup__inputPoints}`}>
        <TextInput
          className='text-sm placeholder:text-sm'
          value={formData.limitOfProductQty}
          onChange={handleLimitOfProductQtyChange}
          label=''
          placeholderText='Qty'
          error={limitOfProductQtyError}
          isDark={true}
          type='number'
          id='bonus-product-limit'
        />
      </div>
      <span className='mt-2 whitespace-nowrap text-sm'>bonus products.</span>

      {/* Product Selection Modal */}
      <ProductSelectionModal
        showModal={showProductModal}
        onCloseModal={handleCloseProductModal}
        onSelectProducts={handleProductSelection}
        selectedProducts={
          formData.selectedProducts?.map((p) => ({
            productId: p.value,
            productName: p.displayText || p.label
          })) || []
        }
      />
    </div>
  );
};

BonusProductsCondition.propTypes = {
  formData: PropTypes.shape({
    selectedProducts: PropTypes.array,
    orderQty: PropTypes.string,
    limitOfProductQty: PropTypes.string
  }).isRequired,
  validation: PropTypes.shape({
    selectedProductsValid: PropTypes.bool
  }).isRequired,
  bonusProductsLoading: PropTypes.bool.isRequired,
  handleProductsChange: PropTypes.func.isRequired,
  handleOrderQtyChange: PropTypes.func.isRequired,
  handleLimitOfProductQtyChange: PropTypes.func.isRequired,
  orderQtyError: PropTypes.string,
  limitOfProductQtyError: PropTypes.string
};

export default BonusProductsCondition;
